<template>
  <v-form class="new-offer-form" ref="form" lazy-validation>
    <v-container fluid class="px-10 pt-10">
      <v-row>
        <v-col cols="12">
          <h3 class="new-offer-form__title"> {{titleForm}}</h3>
        </v-col>

        <v-col cols="12" md="8">
          <!-- Usar alias? -->
          <v-checkbox
            v-model="offer.useAlias"
            class="alias ma-0 pa-0 d-block"
            :label="'Selecciona si necesitas publicar bajo otro nombre'">
          </v-checkbox>
          <v-text-field
            class="new-offer-form__input"
            v-if="offer.useAlias"
            v-model="offer.alias"
            ref="alias"
            name="alias"
            placeholder="Ingresa un nombre ficticio que represente a la organización"
            required
            :error-messages="aliasErrors"
            @input="$v.offer.alias.$touch()"
            @blur="$v.offer.alias.$touch()"
          />

          <!-- Nombre del cargo -->
          <FormQuestionTitle>
            Nombre del cargo
            <template #tooltip>
              Indica el nombre del cargo. Si no es muy descriptivo, haz una bajada que lo ejemplifique mejor en la descripción.
            </template>
          </FormQuestionTitle>
          <v-text-field
            v-model="offer.title"
            ref="title"
            name="title"
            placeholder="Ingresa el nombre"
            hint="Obligatorio"
            counter="60"
            persistent-hint
            required
            :error-messages="titleErrors"
            @input="$v.offer.title.$touch()"
            @blur="$v.offer.title.$touch()"
          />

          <!-- Propósito del cargo -->
          <FormQuestionTitle margin>
            Propósito del cargo
            <template #tooltip>
              Los talentos que verán tu oferta, les interesa encontrar un trabajo con sentido. Por favor especifica acá cómo las tareas que realizará desde este rol aportarán al propósito de la organización. Ej.: "El jefe de comunicaciones XXXXX"
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="purpose"
            name="purpose"
            v-model="offer.purpose"
            placeholder="¿Como contribuye este cargo al propósito de la organización?"
            :extensions="extensions"
            required
            :error-messages="purposeErrors"
            @input="$v.textValidate.purpose.$touch()"
            @blur="$v.textValidate.purpose.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ purposeErrors.length !== 0 ? 'red--text' : ''}`">Obligatorio</div>
                <span>{{getInnerText(offer.purpose).length }}/400</span>
              </div>
              <div :class="`v-messages__message ${ purposeErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in purposeErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- Descripción del cargo -->
          <FormQuestionTitle margin>
            Descripción del cargo
            <template #tooltip>
              Especifica cuáles serán las funciones que tendrá la persona en el cargo y el objetivo que debe buscar alcanzar. Te sugerimos ser lo más claro en cuanto a las tareas y desafíos, ya que eso ayudará al postulante a conectar con tu propuesta.
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="description"
            name="description"
            v-model="offer.description"
            placeholder="Objetivo y funciones del cargo"
            :extensions="extensions"
            required
            :error-messages="descriptionErrors"
            @input="$v.textValidate.description.$touch()"
            @blur="$v.textValidate.description.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ descriptionErrors.length !== 0 ? 'red--text' : ''}`">Obligatorio</div>
                <span>{{getInnerText(offer.description).length }}/3000</span>
              </div>
              <div :class="`v-messages__message ${ descriptionErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in descriptionErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- Nivel del cargo -->
          <FormQuestionTitle>
            Nivel del cargo
            <template #tooltip>
              Indica a qué nivel de cargo corresponde la oferta que estás publicando.
            </template>
          </FormQuestionTitle>
          <v-row align="center">
            <v-col v-for="(level, index) in getLevels" :key="index">
              <v-checkbox class="job-level" v-model="offer.levels" :label="level" :value="level"></v-checkbox>
            </v-col>
          </v-row>

          <!-- Áreas de conocimiento -->
          <FormQuestionTitle>
            Áreas de conocimiento
            <template #tooltip>
              Selecciona con qué areas se identifica el cargo de la oferta. El objetivo es que puedas atraer a los talentos que tienen conocimientos relacionados. Sabemos que la trayectoria muchas veces constituye una gran escuela que complementa los estudios formales.
            </template>
          </FormQuestionTitle>
          <v-select
            ref="areas"
            name="areas"
            placeholder=""
            v-model="offer.areas"
            hint="Obligatorio"
            persistent-hint
            :items="getAreas"
            multiple
            chips
            required
            :error-messages="areasErrors"
            @input="$v.offer.areas.$touch()"
            @blur="$v.offer.areas.$touch()"
          />

          <!-- Requisitos -->
          <FormQuestionTitle margin>
            Requisitos y perfil del cargo
            <template #tooltip>
              Describe brevemente los requisitos (por ejemplo: profesión, especialización, idiomas, conocimiento de software, entre otras) que requiere el cargo, así como habilidades y competencias necesarias (por ejemplo: buen nivel de relaciones interpersonales, capacidad de análisis, tolerancia al estrés, innovación, entre otras).
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="requirements"
            name="requirements"
            v-model="offer.requirements"
            placeholder="Requisitos formales (formación académica) e informales (competencias y habilidades)"
            :extensions="extensions"
            required
            :error-messages="requirementsErrors"
            @input="$v.textValidate.requirements.$touch()"
            @blur="$v.textValidate.requirements.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ requirementsErrors.length !== 0 ? 'red--text' : ''}`">Obligatorio</div>
                <span>{{getInnerText(offer.requirements).length }}/3000</span>
              </div>
              <div :class="`v-messages__message ${ requirementsErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in requirementsErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>

          <!-- Requerimientos deseables -->
          <FormQuestionTitle margin>
            Requerimientos deseables
            <template #tooltip>
              Indica aquí aquellos conocimientos o experiencia que no es requerida de manera obligatoria, pero hará que el postulante mejore sus posibilidades en el proceso.
            </template>
          </FormQuestionTitle>
          <tiptap-vuetify
            ref="optionals"
            name="optionals"
            v-model="offer.optionals"
            placeholder="¿Qué conocimientos o experiencia deseables harán que los postulantes mejoren sus posibilidades?"
            :extensions="extensions"
            :error-messages="optionalsErrors"
            @input="$v.textValidate.optionals.$touch()"
            @blur="$v.textValidate.optionals.$touch()"
          />
          <div class="v-text-field__details">
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper d-flex justify-space-between">
                <div :class="`v-messages__message ${ optionalsErrors.length !== 0 ? 'red--text' : ''}`">Opcional</div>
                <span>{{getInnerText(offer.optionals).length }}/1000</span>
              </div>
              <div :class="`v-messages__message ${ optionalsErrors.length !== 0 ? 'red--text' : ''}`">
                <span v-for="(item, index) in optionalsErrors" :key="index">{{ item }}</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Sección beneficios -->
    <v-container fluid class="new-offer-form__blue-section pa-10">
      <h4 class="new-offer-form__section-title">Beneficios</h4>
      <p class="body-2 text--disabled mr-2">
        Marca los beneficios que tendrá este cargo al trabajar en tu organización. Si tienes otros para agregar, escríbenos
        <a :href="`mailto:hola@trabajoconsentido.com?subject=Agregar Beneficios a -${getUser.organization.name}- en -${offer.title}-`">aquí.</a>
      </p>
      <v-row dense class="ma-0 pa-0">
        <v-col cols="12" sm="12" md="6" lg="6" v-for="(benefit, index) in benefits" :key="index" class="ma-0 px-1 py-0">
          <v-list  dense class="ma-0 pa-0">
            <v-list-item :class="`ma-0 pa-0 ${ offer.benefits.some(item => item === benefit.name) ? 'selected': 'blue lighten-5' }`" link @click="setBenefit(benefit)">
              <v-list-item-avatar class="my-0 ml-0 mr-1">
                <img :src="benefit.image" />
              </v-list-item-avatar>
              <v-list-item-content>
                <h4>{{ benefit.name }}</h4>
                <p class="text--disabled ma-0">{{ benefit.descriptions }}</p>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid class="px-10 pt-10">
      <v-row>
        <v-col cols="12" md="8">
          <!-- Ubicación -->
          <FormQuestionTitle>
            Ubicación del empleo
            <template #tooltip>
              Indica aquí la ciudad y país donde se desempeñará el cargo.
            </template>
          </FormQuestionTitle>
          <v-select
            ref="workingPlace"
            name="workingPlace"
            hint="Obligatorio"
            persistent-hint
            placeholder="Ingresa ciudad o país"
            :error-messages="offerCityErrors"
            v-model="offer.city"
            :items="getCities"
            @input="$v.offer.city.$touch()"
            @blur="$v.offer.city.$touch()"
          />

          <!-- Profesiones -->
          <FormQuestionTitle>
            Profesiones
            <template #tooltip>
              Selecciona las profesiones que consideras adecuadas para el desempeño del cargo.
            </template>
          </FormQuestionTitle>
          <v-select
            ref="professions"
            name="professions"
            placeholder="Selecciona Profesion"
            hint="Obligatorio"
            persistent-hint
            :error-messages="professionsErrors"
            v-model="offer.professions"
            :items="getProfessions"
            multiple
            chips
            @input="$v.offer.professions.$touch()"
            @blur="$v.offer.professions.$touch()"
          />

          <!-- Jornada -->
          <FormQuestionTitle>
              Jornada
              <template #tooltip>
                Identifica el tipo de jornada al cual estará sujeta la persona que ejecute este cargo.
              </template>
            </FormQuestionTitle>
            <v-select
              ref="workingDay"
              name="workingDay"
              hint="Obligatorio"
              persistent-hint
              placeholder="Selecciona la jornada"
              :error-messages="errorWorkingDay"
              v-model="offer.workingDay"
              :items="getWorkingDay"
              @input="$v.offer.workingDay.$touch()"
              @blur="$v.offer.workingDay.$touch()"
            />

          <!-- Modalidad -->
          <FormQuestionTitle>
            Modalidad
            <template #tooltip>
              Identifica la modalidad de trabajo a la cual estará sujeta la persona que ejecute este cargo.
            </template>
          </FormQuestionTitle>
          <v-select
            ref="workingMode"
            name="workingMode"
            hint="Obligatorio"
            persistent-hint
            placeholder="Selecciona la modalidad"
            v-model="offer.workingMode"
            :error-messages="errorWorkingMode"
            :items="getWorkingMode"
            @input="$v.offer.workingMode.$touch()"
            @blur="$v.offer.workingMode.$touch()"
          />

          <!-- Renta -->
          <FormQuestionTitle class="d-inline-block">
            Renta Líquida
            <template #tooltip>
              Indica el rango (min/max) de renta líquida que se ofrece para este cargo. 
              La renta líquida es el monto que recibirá el candidato. La renta bruta, en cambio, 
              es el costo que tendrá para la empresa contratar el cargo (se le suman imposiciones 
              y otros impuestos).
            </template>
          </FormQuestionTitle>
          <p class="txt-field ml-sm-0 ml-md-4 mb-0 d-block d-sm-inline-block">
            La renta “líquida” es la cifra que recibirá la persona después de impuestos.
          </p>
          <div>
            <v-row>
              <v-col cols="10" sm="12">
                <v-col cols="12" class="d-inline-flex ">
                  <span class="ma-0 pl-0 pr-3 py-1">
                    Entre
                  </span>
                  <v-col cols="3" class="ma-0 pa-0">
                    <v-text-field
                      class="ma-0 py-0 px-3"
                      v-model="offer.salary.range[0]"
                      :error-messages="minFieldErrors"
                      @input="$v.minField.$touch()"
                      @blur="$v.minField.$touch()"
                    ></v-text-field>
                  </v-col>
                  <span class="ma-0 px-3 py-0">
                    y
                  </span>
                  <v-col cols="3" class="ma-0 pa-0">
                    <v-text-field
                      class="ma-0 px-3 py-0"
                      v-model="offer.salary.range[1]"
                      :error-messages="maxFieldErrors"
                      @input="$v.maxField.$touch()"
                      @blur="$v.maxField.$touch()"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3" class="ma-0 py-0 px-10">
                    <v-select
                      :items="currency"
                      v-model="offer.salary.currency"
                      hint="Obligatorio"
                      :error-messages="currencyErrors"
                      @input="$v.offer.salary.currency.$touch()"
                      @blur="$v.offer.salary.currency.$touch()"
                      persistent-hint
                    >
                      <template v-slot:label>
                        <p class="text-caption pt-1" v-show="offer.salary.currency === ''">
                          Selecciona una Moneda
                        </p>
                        <!-- <span class="font-weight-thin"></span> -->
                      </template>
                    </v-select>
                  </v-col>
                </v-col>
                <v-col cols="12">
                  <v-checkbox
                    class="job-level ma-0"
                    v-model="offer.salary.hidden"
                    label="No mostrar renta en la publicación (esta información solo la podrá ver tu organización)"
                  >
                  </v-checkbox>
                </v-col>
              </v-col>
            </v-row>
          </div>

          <!-- Años de experiencia -->
          <FormQuestionTitle>
            Años de experiencia
            <template #tooltip>
              Mueve los extremos de la barra para indicar el rango de años de experiencia que se requiere para el cargo.
            </template>
          </FormQuestionTitle>
          <div>
            <v-row>
              <v-col cols="10" sm="6">
                <v-range-slider
                  v-model="offer.experience"
                  :max="Experience[1]"
                  :min="Experience[0]"
                  class="align-center pt-12"
                  thumb-label="always"
                >
                  <template v-slot:thumb-label="{ value }">
                    {{ value > 30 ? '30+' : value }}
                  </template>
                </v-range-slider>
              </v-col>
              <v-col cols="10" sm="4">
                <h4>Entre {{offer.experience[0]}} y {{offer.experience[1] > 30 ? '30+' : offer.experience[1]}} años</h4>
              </v-col>
            </v-row>
          </div>

          <!-- Fecha de incorporación -->
          <FormQuestionTitle>
            Fecha estimada de incorporación al cargo
            <template #tooltip>
              Indica acá la fecha estimada en que se espera que este cargo esté funcionando. El objetivo es poder entregar un plazo estimado a los postulantes respecto al proceso de selección.
            </template>
          </FormQuestionTitle>
          <DatePicker
            placeholder="día/mes/año"
            v-model="startDate"
            :min="getMin"
            :error="dateStartErrors"
            @input="$v.offer.validity.start.$touch()"
            @blur="$v.offer.validity.start.$touch()"
          />

          <!-- Fecha fin postulación -->
          <FormQuestionTitle>
            Fecha de término de la postulación
            <template #tooltip>
              Por política establecemos un período de 60 días en que el aviso estará publicado. Sin embargo, si quieres que termine antes, puedes administrarlo directamente desde el perfil de tu organización.
            </template>
          </FormQuestionTitle>
          <DatePicker
            placeholder="día/mes/año"
            v-model="endDate"
            :min="getMin"
            :error="dateEndErrors"
            :allowedDates="offer.moderatedAt || new Date().toISOString().slice(0, 10)"
            @input="$v.offer.validity.end.$touch() && $v.offer.moderatedAt.touch()"
            @blur="$v.offer.validity.end.$touch() && $v.offer.moderatedAt.touch()"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Sección preguntas -->
    <v-container fluid class="new-offer-form__blue-section pa-10">
      <v-row>
        <v-col cols="12" md="8">

          <v-row justify="space-between">
            <h4 class="new-offer-form__section-title">Preguntas para el postulante</h4>
            <FormQuestionTitle>
              Puedes agregar a continuación hasta 3 preguntas para tus postulantes
              <template #tooltip>
                Ingresa acá las preguntas que quieres que los candidatos respondan. Te sugerimos pensar en aspectos que te ayuden a hacer un primer filtro entre todos los postulantes.
                <br />
                Algunos ejemplos:
                <ul>
                  <li>¿Que aspectos de tu experiencia son importante para este cargo?</li>
                  <li>¿Tienes experiencia  liderando equipos? Comenta por favor.</li>
                </ul>
              </template>
            </FormQuestionTitle>

            <v-col class="pl-0" cols="10">
              <v-text-field
                ref="questions"
                name="questions"
                hint="Obligatorio"
                persistent-hint
                v-model="question"
                :error-messages="questionsErrors"
                @input="$v.offer.questions.$touch()"
                @blur="$v.offer.questions.$touch()"
                placeholder="Escribe aquí tu pregunta a los postulantes (3 máx)"
              />
            </v-col>
            <v-col cols="2">
              <v-btn
                class="new-offer-form__question-add"
                icon
                @click="createQuestion(question)"
                :disabled="question.trim().length === 0 || filteredQuestions.length === 4"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" :md="Math.min(12/filteredQuestions.length, 6)" v-for="question in filteredQuestions" :key="question.id">
          <v-card class="new-offer-form__question">
            <v-card-text v-if="question.id!==0" class="new-offer-form__question-text">
              {{ question.label }}
            </v-card-text>
            <v-card-actions v-if="question.id!==0" class="justify-end">
              <v-btn
                class="new-offer-form__question-btn"
                v-if="!!question.id && question.id!==0 && !isModerated && offer.status !== 'open'"
                @click="removeQuestion(question.id)"
                text
              >ELIMINAR</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid class="pa-10">
      <!-- Errores -->
      <div v-if="errors.length" class="error-form">
        <p>Favor corregir los siguientes errores:</p>
        <ul>
          <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
        </ul>
      </div>

      <!-- Policy -->
      <div class="policy">
        <p class="policy__text">
          Todas las ofertas laborales serán revisadas, según nuestra política de moderación, antes de ser publicadas.
          Nos interesa que los avisos sean bien comprendidos por los posibles candidatos, para asegurar el mejor match posible.
          Al registrarte en nuestra plataforma, estás aceptando que podamos hacer observaciones y solicitar cambios
          o negar la publicación si no se cumple con lo antes indicado.
        </p>
        <v-checkbox v-model="moderationPolicy">
          <template v-slot:label class="policy__label d-block">
            He leído y acepto la <a :href="getPrivacyProtection" target="_blank" rel="noopener noreferrer" class="mx-1">Política de privacidad</a> y la <a :href="getModeration" target="_blank" rel="noopener noreferrer" class="mx-1">Política de moderación</a> de avisos
          </template>
        </v-checkbox>
      </div>

      <!-- Acciones -->
      <v-card-actions class="text-center px-3 mt-8 mb-8">
        <v-row class="justify-md-start justify-center">
          <OfferPreview
            :offer="{...offer, organization: getUser.organization, questions: filteredQuestions}"
            :loading="loading"
          />
          <v-btn
            class="new-offer-form__btn"
            :disabled="$v.$invalid || loading || !moderationPolicy"
            :loading="loading"
            color="info"
            large
            button
            @click="updateOffer"
          >
            ENVIAR A MODERACIÓN
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-container>
  </v-form>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, requiredIf, numeric, integer, helpers } from "vuelidate/lib/validators";
import { TiptapVuetify, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, History } from "tiptap-vuetify";
import { userComputed } from "@/store/helpers.js";
import { fixturesComputed } from "@/store/helpers.js";
import { handleTxt, datetime } from "@/libs/mixins";
import FormQuestionTitle from "@/components/FormQuestionTitle";
import DatePicker from "@/components/DatePicker";
import OfferPreview from "@/components/OfferPreview.vue";

const valiDate = () => helpers.withParams(
    { type: "valiDate" },
    (value, vm) => {
        return new Date(vm.end) < new Date(vm.start);
    }
);

const validateExpired = () => helpers.withParams(
    { type: "validateExpired" },
    (value, vm) => {
        const zeroDay = vm && vm.moderatedAt ? new Date(vm.moderatedAt) : new Date();
        const end = new Date(vm.validity.end);
        let expired =  new Date(zeroDay.getTime() + ((86400000 * 60)));
        const userString = localStorage.getItem("user");
        let user = JSON.parse(userString).user;
        if (user.role === "sysadmin") expired =  new Date(zeroDay.getTime() + ((86400000 * 365)));
        
        return end <= expired;
    }
); 

export default {
    name: "OfferForm",

    props: ["offerObs", "titleForm"],

    components: {
        DatePicker,
        FormQuestionTitle,
        OfferPreview,
        TiptapVuetify,
    },

    mixins: [validationMixin, handleTxt, datetime],

    data() {
        return {
            benefits: [
                { name: "Horario Flexible", descriptions: "Flexibilidad para hacer trámines personales", image: require("@/assets/icons/ico-time.png") },
                { name: "Vestimenta Informal", descriptions: "Puedes vestirte sin formalidades", image: require("@/assets/icons/ico-dress.png") },
                { name: "Computador", descriptions: "Te entregamos computador para hacer el trabajo", image: require("@/assets/icons/ico-computer.png") },
                { name: "Cobertura de salud", descriptions: "Tenemos apoyo complementario de salud", image: require("@/assets/icons/ico-health.png") },
                { name: "Ayuda con reubicación", descriptions: "Te Ayudamos. Si necesitas cambiarte de ciudad", image: require("@/assets/icons/ico-place.png") },
                { name: "Pre y Post natal Adicional a lo legal", descriptions: "", image: require("@/assets/icons/ico-birth.png") },
                { name: "Vacaciones Extras", descriptions: "Hay días adicionales a lo legal", image: require("@/assets/icons/ico-vacations.png") },
            ],
            currency: [
                "USD",
                "CLP"
            ],
            datesFine: false,
            dialog: false,
            endDate: "",
            errors: [],
            Experience: [0, 31],
            extensions: [
                Bold,
                Italic,
                Underline,
                Link,
                ListItem,
                BulletList,
                OrderedList,
                History
            ],
            levels: [],
            loading: false,
            maxField: "",
            minField: "",
            moderationPolicy: false,
            offer: {
                title: "",
                alias: "",
                purpose: "",
                descripFtion: "",
                optionals: "",
                city: "",
                levels: [],
                areas: [],
                requirements: "",
                professions: [],
                benefits: [],
                workingDay: "",
                workingMode: "",
                experience: [1, 3],
                startAt: "",
                salary: {
                    currency: "",
                    hidden: false,
                    range: [0, 0]
                },
                validity: {
                    start: "0/0/0",
                    end: ""
                },
                // questions: [{
                //     label: "Expectativas de sueldo líquido mensual",
                //     id: 0
                // }],
                questions: [{
                    label: "",
                    id: 0,
                    removed: false,
                }],
            },
            question: "",
            startDate: "",
            textValidate: {
                purpose: "",
                description: "",
                requirements: "",
                optionals: "",
            },
            useAlias: false,
        };
    },

    watch: {
        "offer.purpose"() {
            this.textValidate.purpose = this.getInnerText(this.offer.purpose);
        },
        "offer.description"() {
            this.textValidate.description = this.getInnerText(this.offer.description);
        },
        "offer.requirements"() {
            this.textValidate.requirements = this.getInnerText(this.offer.requirements);
        },
        "offer.optionals"() {
            this.textValidate.optionals = this.getInnerText(this.offer.optionals);
        },
        "offer.salary.range"() {
            this.minField = this.offer.salary.range[0];
            this.maxField = this.offer.salary.range[1];
        },
        startDate() {
            this.offer.startAt = this.toDashDate(this.startDate);
            this.offer.validity.start = this.toDashDate(this.startDate);
        },
        endDate() {
            this.offer.validity.end = this.toDashDate(this.endDate);
        },
    },

    computed: {
        ...fixturesComputed,
        ...userComputed,
        getMin() {
            let [year, month, day] = new Date().toISOString().substr(0, 10).split("-");
            let min = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toISOString().substr(0, 10);
            return min;
        },
        aliasErrors() {
            const errors = [];
            if (!this.$v.offer.alias.$dirty) return errors;
            !this.$v.offer.alias.required && errors.push("Alias del cargo requerido");
            return errors;
        },
        areasErrors() {
            const errors = [];
            if (!this.$v.offer.areas.$dirty) return errors;
            !this.$v.offer.areas.required && errors.push("Elija al menos un área de conocimiento");
            // !this.$v.offer.areas.maxLength && errors.push("Elija como máximo 6 áreas de conocimiento");
            return errors;
        },
        currencyErrors() {
            const errors = [];
            if (!this.$v.offer.salary.currency.$dirty) return errors;
            !this.$v.offer.salary.currency.required && errors.push("La moneda es requerida");
            return errors;
        },
        dateStartErrors() {
            const errors = [];
            if (!this.$v.offer.validity.start.$dirty) return errors;
            !this.$v.offer.validity.start.required && !this.$v.offer.validity.start.required && errors.push("Esta fecha es requerida");
            !this.$v.offer.validity.start.valiDate && errors.push("La fecha de incorporación debe ser posterior a la fecha término de postulación");
            return errors;
        },
        dateEndErrors() {
            const errors = [];
            if (!this.$v.offer.validity.end.$dirty) return errors;
            !this.$v.offer.moderatedAt.validateExpired && errors.push("La fecha de termino de postulación no puede exceder los 60 días");
            !this.$v.offer.validity.end.valiDate && errors.push("La fecha de término de postulación debe ser anterior a la fecha de incorporación");
            return errors;
        },
        descriptionErrors() {
            const errors = [];
            if (!this.$v.textValidate.description.$dirty) return errors;
            !this.$v.textValidate.description.required && errors.push("La descripción del cargo es requerida");
            !this.$v.textValidate.description.minLength && errors.push("La descripción debe tener al menos 200 caracteres");
            !this.$v.textValidate.description.maxLength && errors.push("La descripción debe tener máximo 3000 caracteres");
            return errors;
        },
        errorWorkingDay() {
            const errors = [];
            if (!this.$v.offer.workingDay.$dirty) return errors;
            !this.$v.offer.workingDay.required && errors.push("La jornada es requerida");
            return errors;
        },
        errorWorkingMode() {
            const errors = [];
            if (!this.$v.offer.workingMode.$dirty) return errors;
            !this.$v.offer.workingMode.required && errors.push("La modalidad es requerida");
            return errors;
        },
        filteredQuestions() {
            return this.offer.questions.filter(q => q.removed !== true);
        },
        getPrivacyProtection() {
            return `${process.env.VUE_APP_ROOT_ASSETS}${process.env.VUE_APP_ASSETS_OTHERS_PATH}240108+Terminos+y+Condiciones+Trabajo+con+Sentido.pdf`;
        },
        getModeration() {
            // https://ikigai-resources.s3.amazonaws.com/assets/others/Pol%C3%ADtica+de+Moderaci%C3%B3n+de+Ofertas+Laborales+2020+.pdf
            return `${process.env.VUE_APP_ROOT_ASSETS}${process.env.VUE_APP_ASSETS_OTHERS_PATH}Pol%C3%ADtica%20de%20Moderaci%C3%B3n%20de%20Ofertas%20Laborales%202020.pdf`;
        },
        isModerated() {
            return !!this.offer.moderatedAt;
        },
        lastQuestionId() {
            return this.offer.questions[this.offer.questions.length - 1].id;
        },
        minFieldErrors() {
            const errors = [];
            if (!this.$v.minField.$dirty) return errors;
            !this.$v.minField.required && errors.push("Ingrese un mínimo");
            !this.$v.minField.numeric && errors.push("Ingrese solo numeros");
            !this.$v.minField.integer && errors.push("Debe ser un número entero");
            !(parseInt(this.minField) < parseInt(this.maxField)) && errors.push("Debe ser un valor menor al máximo");
            return errors;
        },
        maxFieldErrors() {
            const errors = [];
            if (!this.$v.maxField.$dirty) return errors;
            !this.$v.maxField.required && errors.push("Igrese un máximo");
            !this.$v.maxField.numeric && errors.push("Ingrese solo numeros");
            !this.$v.maxField.integer && errors.push("Debe ser un número entero");
            !parseInt(this.maxField) >= 1 && errors.push("Debe ser mayor a 0");
            !(parseInt(this.minField) < parseInt(this.maxField)) && errors.push("Debe ser un valor mayor al mínimo");
            return errors;
        },
        optionalsErrors() {
            const errors = [];
            if (!this.$v.textValidate.optionals.$dirty) return errors;
            // !this.$v.textValidate.optionals.minLength && errors.push("Requerimientos debe tener al menos 180 caracteres");
            !this.$v.textValidate.optionals.maxLength && errors.push("Los requerimientos deben tener máximo 1000 caracteres");
            return errors;
        },
        purposeErrors() {
            const errors = [];
            if (!this.$v.textValidate.purpose.$dirty) return errors;
            !this.$v.textValidate.purpose.required && errors.push("El propósito del cargo es requerido");
            !this.$v.textValidate.purpose.minLength && errors.push("El propósito debe tener al menos 120 caracteres");
            !this.$v.textValidate.purpose.maxLength && errors.push("El propósito debe tener máximo 400 caracteres");
            return errors;
        },
        professionsErrors() {
            const errors = [];
            if (!this.$v.offer.professions.$dirty) return errors;
            !this.$v.offer.professions.required && errors.push("Elija al menos una profesión");
            return errors;
        },
        questionsErrors() {
            const errors = [];
            if (!this.$v.offer.questions.$dirty) return errors;
            return errors;
        },
        requirementsErrors() {
            const errors = [];
            if (!this.$v.textValidate.requirements.$dirty) return errors;
            !this.$v.textValidate.requirements.required && errors.push("Los requerimientos del cargo son requeridos");
            !this.$v.textValidate.requirements.minLength && errors.push("Los requerimientos deben tener al menos 200 caracteres");
            !this.$v.textValidate.requirements.maxLength && errors.push("Los requisitos y perfil deben tener máximo 3000 caracteres");
            return errors;
        },
        titleErrors() {
            const errors = [];
            if (!this.$v.offer.title.$dirty) return errors;
            !this.$v.offer.title.required && errors.push("El título del cargo es requerido");
            !this.$v.offer.title.minLength && errors.push("El título debe tener al menos 10 caracteres");
            !this.$v.offer.title.maxLength && errors.push("El título debe tener máximo 110 caracteres");
            return errors;
        },
        offerCityErrors() {
            const errors = [];
            if (!this.$v.offer.city.$dirty) return errors;
            !this.$v.offer.city.required && errors.push("La ciudad de la oferta es requerida");
            return errors;
        },
    },

    methods: {
        toSlashDate(value) {
            if (value && value.includes("-")) {
                let [year, month, day] = value.split("-");
                return `${day}/${month}/${year}`;
            }

            return "";
        },
        toDashDate(value) {
            if (value && value.includes("/")) {
                let [day, month, year] = value.split("/");
                return `${year}-${month}-${day}`;
            }

            return "";
        },
        changeDateFormat(value) {
            if (value.includes("/")) {
                const [day, month, year] = value.split("/");
                return `${year}-${month}-${day}`;
            }
            if (value.includes("-")) {
                const [year, month, day] = value.split("-");
                return `${day}/${month}/${year}`;
            }
        },
        isThereUseAlias(value) {
            if (!value) {
                this.offer.alias = "";
            }
        },
        getDate(date) {
            if (!date || date === "") return 0;
            const [year, month, day] = date.split("-");
            return (new Date(year, month - 1, day));
        },
        previewForm() {
            this.dialog = true;
        },
        updateOffer() {
            this.offer.status = "review";
            let org = this.$store.getters["users/getOrganization"];

            this.loading = true;
            this.$store.dispatch("offers/update", {
                organizationId: this.offerObj ? org.id : null,
                offer: this.offer
            }).then(() => {
                this.loading = false;
                /// Se guarda registro de termino de publicación ( solo id no definidos ) ///
                if (!this.offer.id) {
                    this.$store.dispatch("cookies/activity", {
                        path: this.$route.path,
                        activity: "taskComplete"
                    });
                }

                if (this.offerObs && this.offerObs.id) {
                    this.$router.push({ name: "OrganizationDashboard" });
                } else {
                    this.$emit("forward-step");
                }
            }).catch(err => {

                this.$store.dispatch("cookies/activity", {
                    path: this.$route.path,
                    activity: "taskError",
                    args: JSON.stringify(err)
                });

                this.loading = false;
                this.$router.push({ name: "RedirectionRule" });
                this.errors = err;
            });
        },
        createQuestion(question) {
            let newId = 1;
            if (this.offer.questions.length > 0) {
                newId = Math.max(...this.offer.questions.map(question => question.id)) + 1;
            }
            this.offer.questions.push({
                label: question,
                // id: this.offer.questions[this.offer.questions.length - 1].id ? this.offer.questions[this.offer.questions.length - 1].id + 1 : 1,
                id: newId,
                removed: false
            });
            // console.log(this.offer.questions);
            // console.log(this.offer.questions[this.offer.questions.length - 1].id);
            this.question = "";
        },
        setBenefit(benefit) {
            let index = this.offer.benefits.findIndex(item => item === benefit.name);
            if (index === -1) {
                this.offer.benefits.push(benefit.name);
            } else {
                this.offer.benefits.splice(index, 1);
            }
        },
        removeQuestion(id) {
            let updatedQuestion = this.offer.questions.find(q => q.id === id);
            let remainingQuestions = this.offer.questions.filter(q => q.id !== id);
            updatedQuestion.removed = true;
            this.offer.questions = [...remainingQuestions, updatedQuestion];
        },
    },

    validations: {
        offer: {
            alias: { required: requiredIf((offer) => {
                return offer.useAlias;
            }) },
            title: { required, minLength: minLength(10), maxLength: maxLength(120) },
            areas: { required },
            questions: { required },
            startAt: { required },
            salary: {
                currency: { required },
            },
            validity: {
                start: { required, valiDate: valiDate() },
                end: { required, valiDate: valiDate() },
            },
            professions: { required },
            city: { required },
            workingDay: { required },
            workingMode: { required },
            moderatedAt: { validateExpired: validateExpired() }
        },
        textValidate: {
            purpose: { required, minLength: minLength(120), maxLength: maxLength(400) },
            description: { required, minLength: minLength(200), maxLength: maxLength(3000) },
            requirements: { required, minLength: minLength(200), maxLength: maxLength(3000) },
            optionals: { maxLength: maxLength(1000) },
        },
        minField: { required, numeric, integer },
        maxField: { required, numeric, integer },
    },

    created() {
        this.minField = this.offer.salary.range[0];
        this.maxField = this.offer.salary.range[1];
        // if (this.offerObs && typeof this.offerObs.experience !== "object") this.offerObs.experience = [ 0, 1 ];
        if (this.offerObs && this.offerObs.experience.length === 0) this.offerObs.experience = [0, 1];

        if (this.offerObs && this.offerObs.id) {
            this.offer = this.offerObs;
            this.offer.useAlias = this.offerObs.alias !== "" ? true : false;
            this.startDate = this.toSlashDate(this.offer.startAt);
            this.endDate = this.toSlashDate(this.offer.validity.end);
        }
        this.$v.offer.$touch();
        this.$v.offer.moderatedAt.$touch();
        this.$v.offer.validity.$touch();
        this.$v.textValidate.$touch();
        this.$v.minField.$touch();
        this.$v.maxField.$touch();
    },
};
</script>
